import { useLoginInfo } from '@/components/LoginInfo/provider';
import { useCallback, useState } from 'react';

const DRAWER_WIDTH_STORAGE_KEY = 'paymentDetailDrawerWidth';

/**
 * Custom hook to manage drawer width with persistence in localStorage
 * @param defaultWidth - The default width to use if no saved width exists
 * @returns Object containing width state and setter function
 */
export const useDrawerWidth = (defaultWidth: number) => {
  const { currentUser } = useLoginInfo();
  const userId = currentUser?.id;

  // Initialize width from localStorage or use default
  const [width, setWidthState] = useState<number>(() => {
    if (!userId) return defaultWidth;

    const storageKey = `${DRAWER_WIDTH_STORAGE_KEY}_${userId}`;
    const savedWidth = window.localStorage.getItem(storageKey);

    if (savedWidth) {
      const parsedWidth = parseInt(savedWidth, 10);
      // Validate that the saved width is a valid number and >= defaultWidth
      if (!isNaN(parsedWidth) && parsedWidth >= defaultWidth) {
        return parsedWidth;
      }
    }

    return defaultWidth;
  });

  // Wrapper function to save width to localStorage when it changes
  const setWidth = useCallback(
    (newWidth: number) => {
      // Validate the new width - must be >= defaultWidth (minWidth)
      if (isNaN(newWidth) || newWidth < defaultWidth) {
        return;
      }

      setWidthState(newWidth);

      // Save to localStorage
      const storageKey = `${DRAWER_WIDTH_STORAGE_KEY}_${userId}`;
      window.localStorage.setItem(storageKey, newWidth.toString());
    },
    [userId, defaultWidth]
  );

  return { width, setWidth };
};
